import { DestroyRef, inject, Injectable, InjectionToken } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, distinctUntilChanged, map, Observable } from 'rxjs';
import { detectIsIpad } from 'projects/shared/src/lib/core/helpers/user-agent-utils';

export const TTS_ENABLED = new InjectionToken<boolean>('TTS_ENABLED', {
  providedIn: 'root',
  factory: () => true
});

@Injectable({
  providedIn: 'root',
})
export class SpeechService {
  private readonly ttsEnabledConfig = inject(TTS_ENABLED);
  private readonly destroyRef = inject(DestroyRef);
  private initialized = false;
  get isSpeaking(): boolean {
    return this.processingSpeak;
  }

  get readSession(): number {
    return this._readSession;
  }

  private processingSpeak = false;
  private _readSession = 0;

  private utterance: SpeechSynthesisUtterance | null = null;
  private voice: SpeechSynthesisVoice | null = null;
  private isUnlockedForIpad = false;

  private readonly _busyProcessorsCount$ = new BehaviorSubject<number>(0);
  public beginProcessor() {
    this._busyProcessorsCount$.next(this._busyProcessorsCount$.value + 1);
  }
  public endProcessor() {
    this._busyProcessorsCount$.next(Math.max(0, this._busyProcessorsCount$.value - 1));
  }
  public readonly isProcessorBusy$: Observable<boolean> = this._busyProcessorsCount$.pipe(
    map((n) => n > 0),
    distinctUntilChanged()
  );
  public readonly ttsEnabled = this.ttsEnabledConfig && typeof speechSynthesis !== 'undefined';

  private get selectedLanguage(): string {
    return this.translate.currentLang() ?? '';
  }

  constructor(private readonly translate: TranslateService) {
    if (!this.ttsEnabled) return;

    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ lang }) => {
      this.stopSpeaking();
      if (this.initialized) {
        this.selectVoice(lang);
      }
    });
  }

  async initalize(): Promise<boolean> {
    if (!this.ttsEnabled) {
      console.log(
        'tts is DISABLED (SpeechSynthesis API not available or disabled by injection token)'
      );
      return false;
    }
    await this.waitForVoices();
    this.selectVoice(this.selectedLanguage);
    console.log('initialize speech service DONE');
    this.initialized = true;
    return true;
  }

  cleanInstruction(instruction: string): string {
    return instruction
      .replace(/\b(\d+)\/(\d+)\b/g, (_: any, nominator: string, denominator: string) => {
        const fractionKey = 'ItemElementsSpeech.FRACTION_' + denominator;
        const fractionTranslation = this.translate.instant(fractionKey);
        const hasFractionTranslation = fractionTranslation && fractionTranslation !== fractionKey;

        if (!hasFractionTranslation) {
          const ofTranslation = this.translate.instant('ItemElementsSpeech.OP_DE');
          return `${nominator} ${ofTranslation} ${denominator}`;
        }

        const pluralSuffix =
          nominator === '1' ? '' : this.translate.instant('ItemElementsSpeech.FRACTION_PLURAL');

        return `${nominator} ${fractionTranslation}${pluralSuffix}`;
      })
      .replace(/\b(m|cm|km|hm|mm|g|kg)\b/g, (match: string) => {
        return this.translate.instant('ItemElementsSpeech.' + match.toUpperCase());
      })
      .replace(/(\d+)\s*[x×](?=\s*(?:\.{3,}\s*)?(?:=\s*)?\d+)/g, `$1${this.translate.instant('ItemElementsSpeech.MULTIPLICATION')}`)
      .replace(/(\d+)\s*[:](?=\s*(?:\.{3,}\s*)?(?:=\s*)?\d+)/g, `$1${this.translate.instant('ItemElementsSpeech.DIVISION')}`)
      .replace(/(\d+)\s*[+](?=\s*(?:\.{3,}\s*)?(?:=\s*)?\d+)/g, `$1${this.translate.instant('ItemElementsSpeech.ADDITION')}`)
      .replace( /(\d+)\s*[-–−](?=\s*(?:\.{3,}\s*)?(?:=\s*)?\d+)/g, `$1${this.translate.instant('ItemElementsSpeech.SUBTRACTION')}`)
      .replace(/€/g, this.translate.instant('ItemElementsSpeech.EURO'))
      .replace(/=/g, this.translate.instant('ItemElementsSpeech.EQUALS_TO'));
  }

  cleanTaskItemContent(taskItemContent: string): string {
    return taskItemContent;
  }

  async speak(text: string): Promise<void> {
    if (!this.ttsEnabled) return;

    this.stopSpeaking();
    this.processingSpeak = true;

    return new Promise<void>((resolve) => {
      const utt = new SpeechSynthesisUtterance(text);
      if (this.voice) {
        utt.voice = this.voice;
        utt.lang = this.voice.lang;
      } else {
        utt.lang = this.selectedLanguage;
      }
      utt.rate = this.getRate();
      this.utterance = utt;

      utt.onend = () => {
        if (this.utterance === utt) {
          this.utterance = null;
        }
        this.processingSpeak = false;
        resolve();
      };

      utt.onerror = (event) => {
        if (this.utterance === utt) {
          this.utterance = null;
        }
        this.processingSpeak = false;
        if (event.error !== 'canceled') {
          console.error('TTS error:', event.error);
        }
        resolve();
      };

      speechSynthesis.speak(utt);
    });
  }

  async stop(): Promise<void> {
    if (!this.ttsEnabled) return;
    this._readSession++;
    this.stopSpeaking();
  }

  private stopSpeaking(): void {
    if (
      typeof speechSynthesis !== 'undefined' &&
      (speechSynthesis.speaking || speechSynthesis.pending)
    ) {
      speechSynthesis.cancel();
    }
    this.utterance = null;
    this.processingSpeak = false;
  }

  private async waitForVoices(): Promise<void> {
    if (typeof speechSynthesis === 'undefined') return;

    if (speechSynthesis.getVoices().length === 0) {
      await new Promise<void>((resolve) => {
        speechSynthesis.addEventListener('voiceschanged', () => resolve(), { once: true });
        setTimeout(resolve, 3000);
      });
    }
  }

  private selectVoice(language?: string): void {
    if (!language || typeof speechSynthesis === 'undefined') return;

    const voices = speechSynthesis.getVoices();
    if (!voices.length) return;

    const normalize = (lang: string) => lang.toLowerCase().replace('_', '-');

    const langLower = normalize(language);
    const preferredLocale = this.getPreferredLocale(langLower);
    const langPrefix = langLower.split('-')[0];

    const findExact = (locale: string) =>
      voices.find((v) => normalize(v.lang) === normalize(locale));

    let voice = preferredLocale ? findExact(preferredLocale) : undefined;

    if (!voice) {
      voice = findExact(langLower);
    }

    if (!voice) {
      voice = voices.find((v) => normalize(v.lang).split('-')[0] === langPrefix);
    }

    if (!voice) {
      voice = voices.find(v => v.default) ?? voices[0];
    }

    this.voice = voice;
    console.log(`TTS voice selected: ${voice?.name} (${voice?.lang}) for language: ${language}`);
  }

  private getPreferredLocale(language: string): string | undefined {
    switch (language) {
      case 'en':
        return 'en-gb';
      case 'nl':
        return 'nl-nl';
      case 'nl-be':
        return 'nl-be';
      default:
        return undefined;
    }
  }

  private getRate(): number {
    switch (this.selectedLanguage) {
      case 'nl-BE':
        return 0.9;
      case 'nl-NL':
        return 0.95;
      case 'en-GB':
        return 0.85;
      default:
        return 0.9;
    }
  }

  public unlockForIpad(): void {
    if (!detectIsIpad()) return;
    if (this.isUnlockedForIpad) return;
    if (!this.ttsEnabled) return;

    const utterance = new SpeechSynthesisUtterance(' ');
    utterance.volume = 0.01;

    if (this.voice) {
      utterance.voice = this.voice;
      utterance.lang = this.voice.lang;
    } else {
      utterance.lang = this.selectedLanguage;
    }

    utterance.onend = () => console.log('[TTS] unlock utterance ended successfully');
    utterance.onerror = (e) => console.debug('[TTS] unlock utterance ignored/canceled:', e.error);
    speechSynthesis.speak(utterance);
    this.isUnlockedForIpad = true;
  }
}
